const LOGS_KEY = 'candidate_status_logs';
const STATUS_KEY = 'candidate_status_map';   // simple id→status map, persists across navigation
const CHANNEL_NAME = 'candidate_status_updates';

export interface StatusLogEntry {
  id: string;
  status: string;
  previousStatus?: string;
  date: string;           // ISO — when this entry was created
  reason?: string;
  interviewDate?: string;
  jobPositionId?: number;
  jobName?: string;
}

type LogMap = Record<string, StatusLogEntry[]>;
type StatusMap = Record<string, string>;

/* ── Internal helpers ─────────────────────────────────────── */

function getMap(): LogMap {
  try {
    return JSON.parse(localStorage.getItem(LOGS_KEY) || '{}');
  } catch {
    return {};
  }
}

function saveMap(map: LogMap): void {
  localStorage.setItem(LOGS_KEY, JSON.stringify(map));
}

function getStatusMap(): StatusMap {
  try {
    return JSON.parse(localStorage.getItem(STATUS_KEY) || '{}');
  } catch {
    return {};
  }
}

function saveStatusMap(map: StatusMap): void {
  localStorage.setItem(STATUS_KEY, JSON.stringify(map));
}

/* ── Public API ───────────────────────────────────────────── */

export function getCandidateLogs(candidateId: string | number): StatusLogEntry[] {
  return getMap()[String(candidateId)] ?? [];
}

export function getLatestCandidateStatus(candidateId: string | number): string | null {
  // Check the fast status map first
  const statusMap = getStatusMap();
  const fast = statusMap[String(candidateId)];
  if (fast) return fast;
  // Fall back to scanning logs
  const logs = getCandidateLogs(candidateId);
  return logs.length ? logs[logs.length - 1].status : null;
}

/** Appends a new log entry, updates the fast status map, and broadcasts the change. */
export function addCandidateLog(
  candidateId: string | number,
  entry: Omit<StatusLogEntry, 'id'>,
): void {
  const id = String(candidateId);

  // 1. Append to log history
  const map = getMap();
  const existing = map[id] ?? [];
  const newEntry: StatusLogEntry = {
    ...entry,
    id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  };
  existing.push(newEntry);
  map[id] = existing;
  saveMap(map);

  // 2. Update fast status map
  const statusMap = getStatusMap();
  statusMap[id] = newEntry.status;
  saveStatusMap(statusMap);

  // 3. Broadcast to all tabs and same-tab listeners
  broadcastStatusChange(id, newEntry.status, newEntry);
}

/**
 * Seeds the fast status map from an API list response — no log entry is created.
 * Writes the API status for every candidate. If a local log entry exists for a
 * candidate (meaning the user changed status locally), the local value wins.
 */
export function seedCandidateStatusFromApi(
  candidates: Array<{ id: string | number; status: string; date?: string | null; jobPositionId?: number | null; jobName?: string | null }>,
): void {
  const statusMap = getStatusMap();
  const logMap = getMap();
  let changed = false;

  for (const { id, status, date, jobPositionId, jobName } of candidates) {
    const key = String(id);
    // If there are local log entries for this candidate, local wins — don't overwrite
    const hasLocalLogs = (logMap[key]?.length ?? 0) > 0;
    if (!hasLocalLogs) {
      statusMap[key] = status;
      if (status !== 'Open to Connect' && date) {
        logMap[key] = [{
          id: `api-${key}-${status}`,
          status,
          date,
          jobPositionId: jobPositionId ?? undefined,
          jobName: jobName ?? undefined,
        }];
      }
      changed = true;
    }
  }
  if (changed) {
    saveStatusMap(statusMap);
    saveMap(logMap);
  }
}

/* ── BroadcastChannel event bus ──────────────────────────── */

export interface StatusChangeEvent {
  candidateId: string;
  status: string;
  entry: StatusLogEntry;
}

type StatusChangeListener = (event: StatusChangeEvent) => void;

// Same-tab listeners (BroadcastChannel only fires in OTHER tabs)
const sameTabListeners = new Set<StatusChangeListener>();

function broadcastStatusChange(
  candidateId: string,
  status: string,
  entry: StatusLogEntry,
): void {
  const payload: StatusChangeEvent = { candidateId, status, entry };

  // Notify same-tab listeners immediately
  sameTabListeners.forEach((fn) => fn(payload));

  // Notify other tabs via BroadcastChannel
  try {
    const channel = new BroadcastChannel(CHANNEL_NAME);
    channel.postMessage(payload);
    channel.close();
  } catch {
    // BroadcastChannel not supported — same-tab only
  }
}

/** Subscribe to status changes from any component or tab.
 *  Returns an unsubscribe function. */
export function onCandidateStatusChange(
  listener: StatusChangeListener,
): () => void {
  sameTabListeners.add(listener);

  // Also listen for cross-tab events
  let channel: BroadcastChannel | null = null;
  try {
    channel = new BroadcastChannel(CHANNEL_NAME);
    channel.onmessage = (e: MessageEvent<StatusChangeEvent>) => listener(e.data);
  } catch {
    // BroadcastChannel not supported
  }

  return () => {
    sameTabListeners.delete(listener);
    channel?.close();
  };
}
